import { X } from "lucide-react";
import { cn } from "../../lib/utils";

interface LocationCardsProps {
  locations: string[];
  crossedOutCards: Record<string, boolean>;
  toggleCard: (location: string) => void;
  isSpy: boolean;
  currentDeck?: string;
}

// Format location name for image path (remove spaces and convert to lowercase)
export const formatLocationName = (location: string) => {
  return location.replace(/\s+/g, '').toLowerCase();
};

// Function to handle image loading errors
export const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  console.error('Failed to load image:', e.currentTarget.src);
  e.currentTarget.style.display = 'none';
};

export const LocationCards = ({ 
  locations, 
  crossedOutCards, 
  toggleCard,
  isSpy,
  currentDeck = 'locationDeck'
}: LocationCardsProps) => {
  
  // Get the correct image path based on deck type
  const getImagePath = (item: string) => {
    const baseDir = currentDeck === 'animalsDeck'
      ? 'animals'
      : currentDeck === 'countriesDeck'
        ? 'countries'
        : 'locations';
    return `/imgs/${baseDir}/${formatLocationName(item)}.webp`;
  };

  const itemType = currentDeck === 'animalsDeck'
    ? 'animal'
    : currentDeck === 'countriesDeck'
      ? 'country'
      : 'location';

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">
        Possible {currentDeck === 'animalsDeck' ? 'Animals' : currentDeck === 'countriesDeck' ? 'Countries' : 'Locations'}
      </h2>
      {isSpy && (
        <p className="text-gray-400 mb-6">
          Click on a {itemType} to cross it off your list
        </p>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-5 xl:grid-cols-6 gap-2">
        {locations.map((location) => (
          <button
            key={location}
            onClick={() => toggleCard(location)}
            className={cn(
              "relative aspect-[1/1] bg-gray-800 rounded-md p-2 flex items-center justify-center text-center transition-all overflow-hidden group",
              crossedOutCards[location] ? "bg-gray-800/50 text-gray-500" : "hover:bg-gray-700",
            )}
          >
            <div className="absolute inset-0 transition-transform duration-300 group-hover:scale-110">
              <img
                key={location} // Ensure unique key for each location
                src={getImagePath(location)}
                alt={location}
                className="w-full h-full object-cover transition-opacity duration-300"
                onError={handleImageError}
                loading="lazy" // Enable lazy loading for performance
              />
            </div>
            <div className={cn(
              "absolute inset-0 bg-black transition-opacity duration-300",
              "bg-black/50 group-hover:bg-black/30"
            )} />
            <span className="relative text-xs font-medium text-white drop-shadow-lg">{location}</span>
            {crossedOutCards[location] && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                <X className="h-4 w-4 text-red-500 opacity-80" />
                <div className="absolute h-0.5 w-full bg-red-500/70 rotate-45"></div>
                <div className="absolute h-0.5 w-full bg-red-500/70 -rotate-45"></div>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}; 